import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { formatCategoryName } from "../lib/site";
import SectionHeading from "../components/SectionHeading";
import { MaskedLines, FadeIn, Reveal } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const Moq = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="moq-page">
      <PageMeta
        title="MOQ — Minimum Order Quantities"
        description="Transparent tiered MOQs for pickleball paddles, balls, nets, and custom gear directly from our factory."
      />
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <p className="mono-caption mb-8 text-neutral-500">Minimum Order Quantity</p>
          </FadeIn>
          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["MOQ, EXPLAINED", <span key="m" className="text-neutral-300">NO SURPRISES.</span>]} />
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-5 py-20 md:py-28 lg:px-10">
        <SectionHeading number="01" title="What is MOQ?" />
        <Reveal>
          <p className="max-w-2xl text-lg leading-relaxed text-neutral-600">
            MOQ is the minimum order quantity — the smallest production run that keeps factory
            pricing viable. Every product page on this site shows its MOQ upfront, and our inquiry
            builder warns you if your quantity falls below it. First-time buyers can often discuss
            flexible terms with our team.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-px border border-black/10 bg-black/10 sm:grid-cols-3">
          {[
            ["Custom Order MOQ", "Custom colors, graphics, and packaging typically start at the product's base MOQ. New shapes or tooling may require higher minimums — quoted per project."],
            ["Private Label MOQ", "Private label programs start at 300 pieces for paddles and 100 pieces for soft goods, including your logo, packaging, and labels."],
            ["Payment Terms", "50% advance deposit upon order confirmation / PI counter-sign, 50% balance before shipment following QC inspection. Accepted via T/T Wire, Western Union, MoneyGram, L/C, Wise."],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div className="h-full bg-white p-8">
                <p className="font-mono text-xs text-brand-red">0{i + 2}</p>
                <h3 className="mt-4 font-display text-2xl font-medium tracking-tight">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-500">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <SectionHeading number="04" title="Product-wise MOQ" className="mt-24" />
        <Reveal>
          <div className="overflow-x-auto border border-black/10" data-testid="moq-table">
            <table className="w-full min-w-[560px] font-mono text-sm">
              <thead>
                <tr className="bg-brand-ink text-white">
                  {["Product", "Category", "MOQ"].map((h) => (
                    <th key={h} className="px-5 py-4 text-left text-[11px] uppercase tracking-[0.2em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-black/10">
                {products.map((p) => (
                  <tr key={p.id} className="transition-colors hover:bg-brand-smoke">
                    <td className="px-5 py-4 font-sans font-medium">
                      <Link to={`/products/${p.id}`} className="hover:text-brand-red" data-testid={`moq-product-${p.id}`}>{p.name}</Link>
                    </td>
                    <td className="px-5 py-4 text-neutral-500">{formatCategoryName(p.category)}</td>
                    <td className="px-5 py-4 font-bold text-brand-red">{p.moq} {p.moq_unit || "pieces"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal className="mt-16 flex flex-wrap items-center justify-between gap-6 border border-brand-ink p-8">
          <div>
            <h3 className="font-display text-2xl font-medium tracking-tight">Need a lower MOQ?</h3>
            <p className="mt-2 text-sm text-neutral-500">Talk to us — first orders and sample runs are flexible.</p>
          </div>
          <Link to="/inquiry" className="btn-primary" data-testid="moq-ask-btn">
            Ask About MOQ <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Moq;
